import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  ComboboxItem,
  Group,
  Loader,
  Modal,
  ScrollAreaAutosize,
  Select,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";

import { StyledEditModalContent } from "@pages/Patients/Laboratory/components/EditModal/EditModal.styled";

import {
  showLoadingNotification,
  showSuccessNotification,
} from "@utils/showNotification";

import { useUpdateAppointment } from "@hooks/mutations/appointment/useUpdateAppointment";
import { useGetClinicConsultationServices } from "@hooks/queries/clinic/useGetClinicConsultationServices";
import { useGetDoctorsForConsultationService } from "@hooks/queries/user/useGetDoctorsForConsultationService";
import { useQueryErrorHandling } from "@hooks/useQueryErrorHandling";

import { Appointment } from "@customTypes/appointment";


interface ModalProps {
  opened: boolean;
  close: () => void;
  appointment: Partial<Appointment> | null;
}

export const EditFollowUpAppointmentModal = ({
  opened,
  close,
  appointment,
}: ModalProps) => {
  const { t } = useTranslation();
  const timePickerRef = useRef<HTMLInputElement>(null);
  const { data: consultationServicesData } = useGetClinicConsultationServices();

  const [appointmentId, setAppointmentId] = useState("");
  const [doctor, setDoctor] = useState<ComboboxItem | null>(null);
  const [appointmentDate, setAppointmentDate] = useState<Date | null>(
    new Date(new Date().setHours(8, 0, 0, 0))
  );
  const [appointmentCategory, setAppointmentCategory] =
    useState<ComboboxItem | null>(null);
  const [serviceType, setServiceType] = useState<ComboboxItem | null>(null);
  const [paymentMethod, setPaymentMethod] = useState("");
  const [city, setCity] = useState("");
  const [zone, setZone] = useState("");

  const {
    data: doctorsForConsultationData,
    isLoading: isDoctorsForConsultationLoading,
    fetchStatus: doctorsForConsultationFetchStatus,
    isSuccess: isDoctorsForConsultationSuccess,
  } = useGetDoctorsForConsultationService({
    role: "doctor",
    serviceId: serviceType?.value,
    isEnabled: !!serviceType?.value,
  });
  const { mutate: updateAppointment, isLoading: isUpdateAppointmentLoading } =
    useUpdateAppointment();
  const handleErrors = useQueryErrorHandling();

  const handleSubmit = () => {
    const id = showLoadingNotification({});
    const jsFormData = new FormData();
    jsFormData.append("appointment_date", appointmentDate?.toISOString() ?? "");
    jsFormData.append("doctor_assigned", doctor?.value ?? "");
    updateAppointment(
      {
        appointmentId,
        formData: jsFormData,
      },
      {
        onSuccess: () => {
          showSuccessNotification({
            id,
            title: "success",
            message: "update-appointment-success",
          });
          close();
        },
        onError: (res: any) => {
          handleErrors(res, "mutation", id);
        },
      }
    );
  };

  useEffect(() => {
    if (appointment?.id) {
      setAppointmentId(appointment.id);
      setAppointmentCategory({
        label: "",
        value: appointment?.category ?? "",
      });
      setAppointmentDate(new Date(appointment?.appointmentDate ?? new Date()));
      setServiceType({
        value: appointment?.details?.serviceTypeId ?? "",
        label: appointment?.details?.serviceType ?? "",
      });
      setPaymentMethod(appointment?.details?.paymentMethod ?? "");
      setCity(appointment?.details?.city ?? "");
      // @ts-expect-error "zone" does not exist on Teleconsultation, but this operation should be fine
      setZone(appointment?.details?.zone ?? "");
    }
  }, [appointment]);

  return (
    <Modal
      centered
      size={"lg"}
      title={<Text fw={500}>{t("edit-appointment")}</Text>}
      opened={opened}
      onClose={close}
      styles={{ body: { minHeight: "auto" } }}
      scrollAreaComponent={ScrollAreaAutosize}>
      <StyledEditModalContent>
        <Stack>
          <Group align="start" justify="space-between" gap={"2rem"}>
            {/* <Select
              flex={1}
              label={t("appointment-category")}
              placeholder={t("select-category")}
              data={getAppointmentCategoriesValues()}
              value={appointmentCategory?.value ?? null}
              onChange={(_val, option) => {
                setAppointmentCategory(option);
              }}
              allowDeselect={false}
            /> */}
            <DateTimePicker
              flex={1}
              label={t("appointment-date")}
              value={appointmentDate}
              onChange={setAppointmentDate}
              withAsterisk
              timeInputProps={{
                ref: timePickerRef,
                leftSection: (
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    onClick={() => timePickerRef?.current?.showPicker()}>
                    <IconClock size={16} />
                  </ActionIcon>
                ),
                minTime: "08:00:00",
                maxTime: "17:00:00",
              }}
              minDate={new Date()}
            />
            <TextInput
              flex={1}
              disabled
              label={t("payment-method")}
              value={paymentMethod}
              onChange={e => setPaymentMethod(e.target.value)}
            />
          </Group>

          <Group align="start" justify="space-between" gap={"2rem"}>
            <TextInput
              flex={1}
              label={t("service-type")}
              value={serviceType?.label}
              disabled
            />
            <Select
              flex={1}
              label={t("doctor")}
              withAsterisk
              searchable
              description={
                serviceType?.value
                  ? isDoctorsForConsultationSuccess &&
                    !doctorsForConsultationData?.length
                    ? t("no-doctors-for-service")
                    : ""
                  : t("select-service-to-populate-doctors")
              }
              disabled={
                !serviceType ||
                (isDoctorsForConsultationSuccess &&
                  !doctorsForConsultationData?.length)
              }
              placeholder={t("select-doctor")}
              value={doctor?.value ?? null}
              onChange={(_val, option) => setDoctor(option)}
              rightSection={
                isDoctorsForConsultationLoading &&
                doctorsForConsultationFetchStatus === "fetching" ? (
                  <Loader size={16} />
                ) : null
              }
              required
              data={doctorsForConsultationData ?? []}
              comboboxProps={{ zIndex: 999999 }}
            />
          </Group>

          <Group align="start" justify="space-between" gap={"2rem"}>
            <TextInput
              flex={1}
              disabled
              label={t("city")}
              value={city}
              onChange={e => setCity(e.target.value)}
            />
            <TextInput
              flex={1}
              disabled
              label={t("zone")}
              value={
                appointmentCategory?.value === "teleconsultation" ? "N/A" : zone
              }
              onChange={e => setZone(e.target.value)}
            />
          </Group>

          {/* <TextInput
            disabled
            w={"calc(50% - 1rem)"}
            label={t("payment-method")}
            value={paymentMethod}
            onChange={e => setPaymentMethod(e.target.value)}
          /> */}
          {/* <Select
            label={t("payment-method")}
            placeholder={t("select-method")}
            data={getPaymentMethods()}
            value={paymentMethod?.value ?? null}
            onChange={(_val, option) => setPaymentMethod(option)}
          /> */}
        </Stack>

        <Group justify="end" mt={"xl"} pb={"0"}>
          <Button loading={isUpdateAppointmentLoading} onClick={handleSubmit}>
            {t("update")}
          </Button>
        </Group>
      </StyledEditModalContent>
    </Modal>
  );
};
